import { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import { LifeBuoy, Send, Sparkles, UserCog, X, Mail, CheckCircle2, AlertCircle, Brain, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import PageIntro from "../components/PageIntro";
import { toast } from "../components/ui/sonner";

const CATEGORIES = [
  { value: "general", label: "General question" },
  { value: "dubai_market", label: "Dubai real-estate market" },
  { value: "cyprus_market", label: "N. Cyprus real-estate market" },
  { value: "germany_market", label: "Germany real-estate market" },
  { value: "tax_legal", label: "Tax / legal advice" },
  { value: "technical", label: "Technical bug / app issue" },
  { value: "other", label: "Other" },
];

export default function SupportPage() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Welcome to the EQUIA concierge. Ask me anything about your portfolio — *fees you paid, cashflow, Spekulationsfrist timing, visa renewals, or the app itself.* If I can't resolve it, I'll connect you with a human expert.",
      needs_human: false,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showTicket, setShowTicket] = useState(false);
  const [suggestedTopic, setSuggestedTopic] = useState("");
  const [tickets, setTickets] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { loadTickets(); }, []);

  const suggested = [
    "How much is my total monthly cashflow across all assets?",
    "When does the Spekulationsfrist on my Germany assets expire?",
    "What fees did I pay to acquire my Dubai property?",
    "How does the EUR ↔ AED switch work?",
  ];

  const send = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const history = next.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const { data } = await api.post("/support/chat", {
        message: trimmed,
        history,
        session_id: sessionId,
      });
      if (data.session_id) setSessionId(data.session_id);
      setMessages(m => [...m, {
        role: "assistant",
        content: data.answer || "(no answer)",
        needs_human: !!data.needs_human,
        suggested_topic: data.suggested_topic || "",
      }]);
      if (data.needs_human && data.suggested_topic) {
        setSuggestedTopic(data.suggested_topic);
      }
    } catch (err) {
      setMessages(m => [...m, {
        role: "assistant",
        content: "I couldn't reach the concierge service right now. Please try again or open a ticket.",
        needs_human: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="support-page" className="max-w-6xl mx-auto">
      <PageIntro
        pageKey="concierge"
        title="AI Concierge + Human Experts"
        subtitle="An advisor that already knows your portfolio."
        bullets={[
          { icon: Brain, label: "Grounded in your data", text: "Ask about Spekulationsfrist on your Berlin flat, DLD on your Dubai apartment, or cashflow comparisons — it has the numbers." },
          { icon: MessageSquare, label: "Try the suggested prompts", text: "Quick chips below the input give you good starting questions for each jurisdiction." },
          { icon: UserCog, label: "Escalate to a human", text: "When AI hits its limit, hit Contact Human Expert — we deliver your ticket via email and reply within 24h." },
        ]}
      />
      <div className="mb-8">
        <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">Support &amp; Expert Access</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-white tracking-tight">Concierge</h1>
        <p className="text-white/60 text-sm mt-1">
          AI assistance grounded in your portfolio. Escalate to a human expert anytime.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* ============= CHAT ============= */}
        <div className="col-span-12 lg:col-span-8" data-testid="concierge-chat">
          <div className="bg-[#0A0A0A] border border-white/[0.10] flex flex-col h-[72vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.10] bg-[#050505]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-white text-sm font-serif">EQUIA Concierge</p>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/65">AI · Portfolio-aware</p>
                </div>
              </div>
              <button
                onClick={() => { setSuggestedTopic(""); setShowTicket(true); }}
                data-testid="open-ticket-btn"
                className="flex items-center gap-2 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] text-xs px-3 py-1.5 transition-colors"
              >
                <UserCog className="w-3.5 h-3.5" />
                Contact Human Expert
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#D4AF37]/10 text-white border border-[#D4AF37]/20"
                      : "bg-white/[0.02] text-white/90 border border-white/[0.10]"
                  }`}
                    data-testid={`chat-msg-${m.role}-${i}`}>
                    {m.role === "assistant" ? (
                      <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-strong:text-[#D4AF37]">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    )}
                    {m.role === "assistant" && m.needs_human && (
                      <button
                        onClick={() => { setSuggestedTopic(m.suggested_topic || ""); setShowTicket(true); }}
                        data-testid={`escalate-btn-${i}`}
                        className="mt-3 flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1.5 transition-colors"
                      >
                        <UserCog className="w-3 h-3" /> Escalate to human expert
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.02] border border-white/[0.10] px-4 py-3 flex gap-1.5">
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse" />
                    <span className="w-1.5 h-1.5 bg-[#D4AF37]/70 rounded-full animate-pulse [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 bg-[#D4AF37]/50 rounded-full animate-pulse [animation-delay:300ms]" />
                  </div>
                </div>
              )}
            </div>

            {/* Suggested prompts */}
            {messages.length <= 1 && !sending && (
              <div className="px-5 py-3 border-t border-white/[0.10] bg-[#050505]" data-testid="suggested-prompts">
                <p className="text-[9px] tracking-[0.15em] uppercase text-white/65 mb-2">Try</p>
                <div className="flex flex-wrap gap-2">
                  {suggested.map((s, i) => (
                    <button key={i} onClick={() => send(s)}
                      className="text-left text-[11px] text-white/85 hover:text-[#D4AF37] border border-white/[0.10] hover:border-[#D4AF37]/30 bg-white/[0.02] px-3 py-1.5 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={e => { e.preventDefault(); send(input); }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/[0.10] bg-[#050505]"
            >
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask about your portfolio…"
                data-testid="chat-input"
                className="flex-1 bg-[#0A0A0A] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none transition-all"
              />
              <button type="submit" disabled={!input.trim() || sending}
                data-testid="chat-send-btn"
                className="bg-[#D4AF37] hover:bg-[#b89430] text-black px-4 py-2.5 text-sm font-medium tracking-wide transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* ============= SIDE PANEL ============= */}
        <aside className="col-span-12 lg:col-span-4 space-y-4">
          {/* Direct expert access */}
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-5">
            <div className="flex items-center gap-2 mb-3">
              <LifeBuoy className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">Direct Expert</p>
            </div>
            <p className="text-white/90 text-sm leading-relaxed mb-4 font-serif">
              Prefer a human first? Open a ticket — our experts respond within one business day.
            </p>
            <button
              onClick={() => { setSuggestedTopic(""); setShowTicket(true); }}
              data-testid="sidebar-open-ticket"
              className="w-full bg-[#D4AF37] hover:bg-[#b89430] text-black text-sm font-medium py-2.5 transition-colors flex items-center justify-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" />
              Open a ticket
            </button>
          </div>

          {/* My tickets */}
          <div className="bg-[#0A0A0A] border border-white/[0.10] p-5" data-testid="my-tickets-panel">
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-3">My Tickets</p>
            {tickets.length > 0 ? (
              <div className="space-y-2">
                {tickets.slice(0, 5).map(t => (
                  <div key={t.id} className="bg-white/[0.02] border border-white/[0.10] p-3" data-testid={`ticket-${t.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-white text-xs truncate flex-1">{t.subject}</p>
                      <span className={`text-[9px] tracking-wider uppercase px-1.5 py-0.5 flex-shrink-0 ${
                        t.status === "resolved"
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      }`}>{t.status}</span>
                    </div>
                    <p className="text-white/60 text-[10px]">{new Date(t.created_at).toLocaleDateString()} · {t.category}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/65 text-xs">No tickets yet.</p>
            )}
          </div>
        </aside>
      </div>

      {/* ============= TICKET MODAL ============= */}
      <TicketModal
        open={showTicket}
        suggestedTopic={suggestedTopic}
        transcript={messages}
        onClose={() => setShowTicket(false)}
        onSubmitted={() => { setShowTicket(false); loadTickets(); }}
      />
    </div>
  );
}

function TicketModal({ open, onClose, onSubmitted, suggestedTopic, transcript }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [description, setDescription] = useState("");
  const [includeTranscript, setIncludeTranscript] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open) {
      setSubject(suggestedTopic || "");
      setDescription("");
      setCategory("general");
      setSuccess(null);
      setIncludeTranscript(true);
    }
  }, [open, suggestedTopic]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;
    setLoading(true);
    try {
      const payload = {
        subject: subject.trim(),
        category,
        description: description.trim(),
        transcript: includeTranscript
          ? transcript.slice(-12).map(m => ({ role: m.role, content: m.content }))
          : [],
      };
      const { data } = await api.post("/support/ticket", payload);
      setSuccess(data);
      setTimeout(() => onSubmitted(), 1500);
    } catch (err) {
      toast.error("Could not open the ticket", { description: err.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 z-50" />
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
            className="fixed inset-x-4 top-12 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[560px] z-50 bg-[#0A0A0A] border border-[#D4AF37]/20 overflow-hidden"
            data-testid="ticket-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.10] bg-[#050505]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                  <UserCog className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-white text-sm font-serif">Contact a Human Expert</p>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/65">One-click ticket</p>
                </div>
              </div>
              <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="ticket-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {success ? (
              <div className="p-10 text-center">
                <div className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
                </div>
                <p className="text-white font-serif text-lg mb-1">Ticket opened</p>
                <p className="text-white/65 text-xs mb-3">
                  Reference: <span className="font-mono">{success.id?.slice(0, 8)}</span>
                </p>
                <p className="text-white/60 text-sm">
                  {success.email_status === "sent"
                    ? "An EQUIA expert has been notified and will respond shortly."
                    : "Stored securely. Our team will reply from your profile email."}
                </p>
                {success.email_status === "failed" && (
                  <p className="mt-2 text-amber-400 text-xs flex items-center gap-1.5 justify-center">
                    <AlertCircle className="w-3 h-3" /> Email notification delayed — your ticket is still recorded.
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Subject</label>
                  <input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    data-testid="ticket-subject"
                    placeholder="e.g. Dubai market outlook for Q3"
                    className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Category</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    data-testid="ticket-category"
                    className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none transition-all"
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Describe your issue</label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    data-testid="ticket-description"
                    rows={5}
                    placeholder="Tell us as much as you can — we reply within one business day."
                    className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none transition-all resize-none"
                    required
                  />
                </div>
                {transcript && transcript.length > 1 && (
                  <label className="flex items-center gap-2 text-white/85 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeTranscript}
                      onChange={e => setIncludeTranscript(e.target.checked)}
                      data-testid="ticket-include-transcript"
                      className="accent-[#D4AF37]"
                    />
                    Include recent chat transcript for context
                  </label>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button type="button" onClick={onClose}
                    className="px-4 py-2.5 text-sm text-white/65 hover:text-white transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading || !subject.trim() || !description.trim()}
                    data-testid="ticket-submit"
                    className="bg-[#D4AF37] hover:bg-[#b89430] text-black text-sm font-medium px-5 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> {loading ? "Sending…" : "Send to expert"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
